// ============================================================
// actionTypes.constants.ts
// SINGLE SOURCE OF TRUTH for the activity action-type vocabulary.
//
// Previously the same list was hand-copied across three files —
// ACTION_VARIANTS (AddActivityModal), ACTION_COLOUR_MAP (TimelineRow)
// and ACTIVITY_TYPE_FILTERS (ClientSearchView) — which would drift the
// moment new types (fax / email / SMS, the greyed-out toolbar actions)
// get wired up. All three now DERIVE from ACTION_TYPES below, so
// adding a type is a one-line change HERE and it flows to the filter
// buttons, the row colours, and the add-activity picker.
//
// Fields:
//   value       — stored snake_case actionType string (payload +
//                 backend `actionType` filter value). This IS the
//                 vocabulary key.
//   label       — human-readable display label (row pill + filter
//                 button), e.g. 'Take call'.
//   stripe      — FULL Tailwind left-border class for the row accent
//                 stripe (e.g. 'border-l-blue-400'). Kept as a full
//                 literal — NOT interpolated — so Tailwind's content
//                 scanner keeps it; constructed class names get purged.
//   pill        — FULL Tailwind bg + text classes for the type pill.
//   pickerLabel — the add-activity type-picker verb phrase (e.g.
//                 'Make call'), which may differ from `label`.
//
// Colour rationale (unchanged): call = blue-400 (outbound, cool);
// take_call = sky-300 (inbound, softer); letter + memo = casevo-mid
// (in-brand written work).
// ============================================================
export interface ActionTypeDef {
  value: string
  label: string
  stripe: string
  pill: string
  pickerLabel: string
}

export const ACTION_TYPES = [
  { value: 'call',      label: 'Call',      stripe: 'border-l-blue-400',   pill: 'bg-blue-400/15 text-blue-400',     pickerLabel: 'Make call' },
  { value: 'take_call', label: 'Take call', stripe: 'border-l-sky-300',    pill: 'bg-sky-300/15 text-sky-300',       pickerLabel: 'Take call' },
  { value: 'letter',    label: 'Letter',    stripe: 'border-l-casevo-mid', pill: 'bg-casevo-mid/15 text-casevo-mid', pickerLabel: 'Letter' },
  { value: 'memo',      label: 'Memo',      stripe: 'border-l-casevo-mid', pill: 'bg-casevo-mid/15 text-casevo-mid', pickerLabel: 'Memo' },
  // Email (Microsoft Stage 3): logged when a solicitor sends from their own
  // mailbox via the compose modal. Distinct purple accent so a sent email
  // reads apart from calls (blue) and written work (casevo-mid).
  { value: 'email',     label: 'Email',     stripe: 'border-l-purple-400', pill: 'bg-purple-400/15 text-purple-400', pickerLabel: 'Send email' },
] as const satisfies readonly ActionTypeDef[]

// Union of the stored actionType values — for typing filter state and
// variant configs so they can't reference a type that doesn't exist.
export type ActionTypeValue = typeof ACTION_TYPES[number]['value']

// value → def lookup, typed as a Record over the value union so
// indexing with a non-vocabulary string is a COMPILE error. This is
// the compile-time link that stops the modal drifting from ACTION_TYPES
// (e.g. a typo'd 'takecall' won't resolve).
export const ACTION_TYPE_BY_VALUE = Object.fromEntries(
  ACTION_TYPES.map((t) => [t.value, t]),
) as Record<ActionTypeValue, ActionTypeDef>
